'use client';

import React, { useEffect, useState } from 'react';
import BaseMechanicShell from './BaseMechanicShell';
import { Network } from 'lucide-react';
import { useMountShuffledList } from './shuffle-utils';

interface AbstractTransferMechanicProps {
  lessonSlug: string;
  mechanicId?: string;
  id?: string;
  prompt?: string;
  tier?: string;
  localHints?: { id: string; hint: string }[];
  localValidator?: { kind?: string; correctAnswer?: string; hints?: any[] };
  payload: {
    sourceDomain?: string;
    targetDomain?: string;
    scenario?: string;
    options?: any[];
    _hasHints?: boolean;
  };
}

export default function AbstractTransferMechanic({ lessonSlug, mechanicId, id, prompt, tier, localHints, localValidator, payload }: AbstractTransferMechanicProps) {
  const actualMechanicId = mechanicId || id || '';

  return (
    <BaseMechanicShell
      lessonSlug={lessonSlug}
      mechanicId={actualMechanicId}
      prompt={payload?.scenario || prompt || ''}
      icon={<Network className="w-5 h-5" />}
      iconLabel="Abstract Transfer — Apply concepts from one domain to another"
      payload={payload}
      tier={tier}
      localHints={localHints}
      localValidator={localValidator}
    >
      {(submissionState, setAnswer) => (
        <AbstractTransferMechanicInner payload={payload} submissionState={submissionState} setAnswer={setAnswer} />
      )}
    </BaseMechanicShell>
  );
}


function AbstractTransferMechanicInner({ payload, submissionState, setAnswer }: any) {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const sourceDomain = payload?.sourceDomain || "Source Domain";
  const targetDomain = payload?.targetDomain || "Target Domain";
  const scenario = payload?.scenario || "";
  const options = useMountShuffledList(payload?.options || []);

  useEffect(() => {
    setAnswer(selectedOption);
  }, [selectedOption, setAnswer]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-4 bg-muted/30 p-4 rounded-xl border border-border/50">
        <div className="flex-1 text-center">
          <h4 className="text-xs uppercase font-bold text-muted-foreground mb-1">Source Domain</h4>
          <div className="bg-background border border-border rounded py-2 px-3 text-sm font-medium">
            {sourceDomain}
          </div>
        </div>
        <div className="text-primary font-bold">→</div>
        <div className="flex-1 text-center">
          <h4 className="text-xs uppercase font-bold text-muted-foreground mb-1">Target Domain</h4>
          <div className="bg-primary/10 border border-primary/30 text-primary rounded py-2 px-3 text-sm font-medium">
            {targetDomain}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {options.map((opt: any) => {
          const isSelected = selectedOption === opt.id;
          return (
            <button
              type="button"
              key={opt.id}
              onClick={() => setSelectedOption(opt.id)}
              disabled={submissionState !== 'idle' && submissionState !== 'error'}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                isSelected
                  ? 'border-primary bg-primary/5 ring-2 ring-primary/20 shadow-sm'
                  : 'border-border/50 bg-card hover:border-primary/50 hover:bg-muted/30'
              }`}
            >
              <p className="text-sm leading-relaxed">{opt.content}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
